import {
  Box,
  ListSubheader,
  MenuItem,
  Select,
  styled,
  Typography,
} from '@mui/material';
import CurrencyRate from 'components/CurrencyRate';
import { ActionTransactionSubType } from 'further-types/lp/action';
import {
  CurrencyRateDetails,
  TransactionOperation,
} from 'further-types/lp/transaction';
import { CurrencyCode, FxDataFeed } from 'further-types/lp/vehicle';
import ColorIndicator from 'further-ui/components/ColorIndicator';
import { Column, Row } from 'further-ui/components/Layout';
import Table, { type Column as TableColumn } from 'further-ui/components/Table';
import {
  calculateLcyValue,
  numberToCurrencyString,
  stringToColor,
} from 'further-ui/utils';
import { ReactNode, useEffect } from 'react';

const StyledBox = styled(Box)({
  width: '100%',
});

const Dot = styled(ColorIndicator)(({ theme }) => ({
  margin: theme.spacing(theme.spacingSizes.xs),
}));

const GreySelect = styled(Select)(({ theme }) => ({
  '&& .MuiSelect-select.Mui-disabled': {
    WebkitTextFillColor: theme.palette.grey[500],
  },
}));

type InputTransaction = {
  id?: string;
  actionId?: string;
  transactionTypeId: string;
  operationType: TransactionOperation;
  amountExVatLcy?: number | null;
  amountIncVatLcy?: number | null;
  amountExVatTcy: number;
  amountIncVatTcy: number;
  transactionCurrencyRate?: CurrencyRateDetails;
  transactionCurrency: CurrencyCode;
  transactionSubType: ActionTransactionSubType;
  preCalculatedAmounts?: boolean;
  label?: string;
  commitmentId?: string;
};

export type Transaction<T extends InputTransaction = InputTransaction> = T;

type Props<T extends InputTransaction> = {
  transactions: Array<T>;
  transactionTypeOptions: Array<{
    label: string;
    value: string | null;
  }>;
  onChange?: (transactions: Array<Transaction<T>>) => void;
  transactionCurrency: CurrencyCode;
  vehicle: {
    _id: string;
    baseCurrency: CurrencyCode;
    fxDataFeed: FxDataFeed;
  };
  title?: string;
  subtitle?: string;
  disableRateChange?: boolean;
  readOnly?: boolean;
  fxRate: CurrencyRateDetails | null;
  showCurrencyRate?: boolean;
  forceShowTcy?: boolean;
  transactionsInFuture?: boolean;
  forceLcyCalculation?: boolean;
  currencyRateComponent?: ReactNode;
  darkBackground?: boolean;
};

const TransactionAllocator = <T extends InputTransaction>({
  transactions,
  transactionTypeOptions,
  onChange,
  transactionCurrency,
  vehicle,
  fxRate,
  title,
  subtitle,
  disableRateChange = false,
  readOnly = false,
  showCurrencyRate = false,
  forceShowTcy = false,
  transactionsInFuture = false,
  forceLcyCalculation = false,
  currencyRateComponent,
  darkBackground = false,
}: Props<T>) => {
  const isMulticurrency = transactionCurrency !== vehicle.baseCurrency;

  const shouldShowTcyValues = isMulticurrency || forceShowTcy;

  const printLabel = (label?: string) => {
    if (!label) return '';
    return `(${label})`;
  };

  const hasUnknownFutureRate =
    transactionsInFuture && !fxRate && isMulticurrency;

  const computeLcyAmount = (
    amountTcy: number,
    rateDetails?: CurrencyRateDetails | null,
    amountLcy?: number | null,
  ) => {
    if ((disableRateChange || readOnly) && amountLcy && !forceLcyCalculation) {
      return amountLcy;
    }

    if (hasUnknownFutureRate) {
      return null;
    }

    return shouldShowTcyValues && rateDetails
      ? calculateLcyValue(amountTcy, rateDetails)
      : amountTcy;
  };

  const buildTransactions = (transactions: Array<T>) =>
    transactions.map((t) => ({
      ...t,
      amountIncVatTcy: t.amountIncVatTcy,
      amountExVatTcy: t.amountExVatTcy,
      amountIncVatLcy: t.preCalculatedAmounts
        ? t.amountIncVatLcy ?? 0
        : computeLcyAmount(t.amountIncVatTcy, fxRate, t.amountIncVatLcy),
      amountExVatLcy: t.preCalculatedAmounts
        ? t.amountExVatLcy ?? 0
        : computeLcyAmount(t.amountExVatTcy, fxRate, t.amountExVatLcy),
      transactionCurrency: t.transactionCurrency ?? transactionCurrency,
      transactionCurrencyRate: t.transactionCurrencyRate ?? fxRate ?? undefined,
    }));

  const sortTransactionsByOperationType = <T extends InputTransaction>(
    transactions: Array<Transaction<T>>,
  ): Array<Transaction<T>> => {
    return [...transactions].sort((a, b) => {
      if (
        a.operationType === TransactionOperation.Debit &&
        b.operationType === TransactionOperation.Credit
      ) {
        return -1;
      }
      if (
        a.operationType === TransactionOperation.Credit &&
        b.operationType === TransactionOperation.Debit
      ) {
        return 1;
      }

      // If same operation type, sort by amountIncVatTcy (largest first)
      const tcyDiff = b.amountIncVatTcy - a.amountIncVatTcy;
      if (tcyDiff !== 0) {
        return tcyDiff;
      }

      // If amountIncVatTcy is the same, sort by amountIncVatLcy (largest first)
      return (b.amountIncVatLcy ?? 0) - (a.amountIncVatLcy ?? 0);
    });
  };

  useEffect(() => {
    if (!transactions) return;
    onChange?.(buildTransactions(transactions));
  }, [fxRate]);

  const builtTransactions = buildTransactions(transactions);
  const sortedBuiltTransactions =
    sortTransactionsByOperationType(builtTransactions);

  // Create a mapping from sorted index to original index
  // Must be computed before the .map() below, which creates new object references
  const sortedToOriginalIndexMap = sortedBuiltTransactions.map(
    (sortedTransaction) =>
      builtTransactions.findIndex(
        (originalTransaction) => originalTransaction === sortedTransaction,
      ),
  );

  const sortedTransactions = sortedBuiltTransactions.map((t, index) => ({
    ...t,
    id: t.id ?? `${t.operationType}-${t.transactionSubType}-${index}`,
  }));

  const columns: Array<TableColumn<(typeof sortedTransactions)[number]>> = [
    {
      key: 'transactionTypeId',
      label: 'General ledger account and transaction type',
      width: '600px',
      render: (_: unknown, displayIndex: number) => {
        const originalIndex = sortedToOriginalIndexMap[displayIndex];
        const SelectComponent = darkBackground ? GreySelect : Select;
        return (
          <SelectComponent
            value={sortedTransactions[displayIndex].transactionTypeId}
            fullWidth
            disabled={readOnly}
            onChange={(e) => {
              const updatedTransactions = [...transactions];
              updatedTransactions[originalIndex].transactionTypeId =
                e.target.value;
              onChange?.(buildTransactions(updatedTransactions));
            }}
            renderValue={(value) => {
              const option = transactionTypeOptions.find(
                (option) => option.value === value,
              );
              return (
                <>
                  <strong>{option?.label.split('-')[0]}</strong> -{' '}
                  {option?.label.split('-')[1]}
                </>
              );
            }}
          >
            {transactionTypeOptions.map((option) =>
              !option.value ? (
                <ListSubheader key={option.label}>{option.label}</ListSubheader>
              ) : (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ),
            )}
          </SelectComponent>
        );
      },
    },
    {
      key: 'debitAmount',
      label: shouldShowTcyValues ? 'Debit (TCY)' : 'Debit amount',
      render: (row: InputTransaction) =>
        row.operationType === TransactionOperation.Debit
          ? `${numberToCurrencyString(row.amountIncVatTcy, {
              currency: row.transactionCurrency ?? transactionCurrency,
            })} ${printLabel(row.label)}`
          : '-',
    },
    {
      key: 'creditAmount',
      label: shouldShowTcyValues ? 'Credit (TCY)' : 'Credit amount',
      render: (row: InputTransaction) =>
        row.operationType === TransactionOperation.Credit
          ? `${numberToCurrencyString(row.amountIncVatTcy, {
              currency: row.transactionCurrency ?? transactionCurrency,
            })} ${printLabel(row.label)}`
          : '-',
    },
    ...(shouldShowTcyValues
      ? [
          {
            key: 'debitAmountLcy',
            label: 'Debit (LCY)',
            render: (row: InputTransaction) =>
              row.operationType === TransactionOperation.Debit
                ? `${numberToCurrencyString(row.amountIncVatLcy, {
                    currency: vehicle.baseCurrency,
                  })} ${printLabel(row.label)}`
                : '-',
          },
          {
            key: 'creditAmountLcy',
            label: 'Credit (LCY)',
            render: (row: InputTransaction) =>
              row.operationType === TransactionOperation.Credit
                ? `${numberToCurrencyString(row.amountIncVatLcy, {
                    currency: vehicle.baseCurrency,
                  })} ${printLabel(row.label)}`
                : '-',
          },
        ]
      : []),
  ];

  const titleText = title ?? 'Account transactions';

  const showCurrencyRateSection =
    isMulticurrency && (!!currencyRateComponent || showCurrencyRate);
  const showHeading = !!title || !!subtitle;
  const showTitle = showCurrencyRateSection || showHeading;

  const titleComponent = showTitle ? (
    <Column spacing="md">
      {showHeading && (
        <Row centered spacing="xs">
          <Dot color={stringToColor(`${titleText}-${subtitle}`)} />
          <Typography variant="h6">{titleText}</Typography>
          {subtitle && <Typography variant="body2">{subtitle}</Typography>}
        </Row>
      )}
      {showCurrencyRateSection &&
        (currencyRateComponent ? (
          currencyRateComponent
        ) : (
          <CurrencyRate
            fxRate={fxRate}
            currency={transactionCurrency}
            vehicle={vehicle}
            readOnly
            isFetchingRate={false}
          />
        ))}
    </Column>
  ) : undefined;

  return (
    <StyledBox>
      <Table
        title={titleComponent}
        columns={columns}
        rows={sortedTransactions}
        darkBackground={darkBackground}
      />
    </StyledBox>
  );
};

export default TransactionAllocator;
